import React from 'react';

import { cn } from '@cineamo/ui/src/helpers/cn';

export type MovieDetailRow = {
    label: string | React.ReactNode;
    value: string | React.ReactNode;
};

/**
 * Pure presentational component for displaying movie details in a table format.
 * Shows label-value pairs with dividers between rows.
 * Supports two variants: default (two-column) and compact (stacked).
 */
const MovieDetailsTable: React.FC<{
    className?: string;
    details: MovieDetailRow[];
    variant?: 'default' | 'compact';
}> = ({ className, details, variant = 'default' }) => {
    const filteredDetails = details.filter((detail) => detail.value !== undefined);

    if (filteredDetails.length === 0) {
        return null;
    }

    if (variant === 'compact') {
        // Compact variant for info dropdown
        return (
            <div className={cn(className, 'flex flex-col space-y-24')}>
                {filteredDetails.map((detail, index) => (
                    <div
                        key={index}
                        className="flex flex-col space-y-4 text-body-14">
                        <div className="text-body-bold-10 text-white/70 uppercase">{detail.label}</div>
                        <div>{detail.value}</div>
                    </div>
                ))}
            </div>
        );
    }

    // Default variant for header box
    return (
        <div className={cn(className, 'lg:col-span-2')}>
            <div className="w-full h-auto bg-white/10 p-16 rounded-8">
                {filteredDetails.map((detail, index) => (
                    <div
                        key={index}
                        className="flex flex-col space-y-12">
                        <div className={cn('w-full flex flex-row items-center', index !== 0 && 'pt-12')}>
                            <div className="w-1/2 text-body-bold-12">{detail.label}</div>
                            <div className="w-1/2 text-white/70 text-body-12 font-montserrat-medium">
                                {detail.value}
                            </div>
                        </div>
                        {index !== filteredDetails.length - 1 && <div className="w-full h-1 bg-white/20" />}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default MovieDetailsTable;
